import { useDialogContext } from '@ariakit/react'
import { type FC, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	DialogDisclosure,
	DialogDismiss,
	DialogFooter,
	DialogHeader,
	DialogHeading,
	DialogProvider
} from '@/components/ui/dialog-atoms'
import { ThumbsDownIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import type {
	ExperienceFlag,
	ExperienceGalleryImage
} from '@/features/experience-shell/types.ts'
import { useAddExperienceGalleryImageFlags } from '@/features/experience-vetting/queries/use-add-experience-gallery-image-flags.ts'
import { useUpdateExperienceVettingImageIsApproved } from '@/features/experience-vetting/queries/use-update-experience-vetting-image-is-approved.ts'
import {
	OpsVettingRejectDialog,
	OpsVettingRejectDialogBody,
	OpsVettingRejectDialogBodyFlagsItem
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n'
import { useGetExperiencesFlags } from '../http/use-get-experiences-flags'
import { useRemoveExperienceGalleryImageFlags } from '../queries/use-remove-experience-gallery-image-flags'
import { experiencesFlagsMapToLabel } from '../utils'

interface ExperienceVettingGalleryImageRejectDialogProps {
	image: ExperienceGalleryImage
	experienceId: number
	galleryId: number
	currentFlags: ExperienceFlag[]
	disabled?: boolean
}

export const ExperienceVettingGalleryImageRejectDialog = ({
	image,
	experienceId,
	galleryId,
	currentFlags,
	disabled
}: ExperienceVettingGalleryImageRejectDialogProps) => {
	const { t } = useTranslation()

	return (
		<DialogProvider>
			<DialogDisclosure
				render={
					<ButtonLegacy
						className='w-full'
						disabled={disabled}
						size='sm'
						type='button'
						variant='secondary'
					>
						<ThumbsDownIcon />
						{t('vetting:criteria.commons.reject')}
					</ButtonLegacy>
				}
			/>
			<OpsVettingRejectDialog hideOnInteractOutside={false} width='600px'>
				<ExperienceVettingGalleryImageRejectDialogBody
					currentFlags={currentFlags}
					experienceId={experienceId}
					galleryId={galleryId}
					image={image}
				/>
			</OpsVettingRejectDialog>
		</DialogProvider>
	)
}

export const ExperienceVettingGalleryImageRejectDialogBody: FC<
	ExperienceVettingGalleryImageRejectDialogProps
> = ({ image, experienceId, galleryId, currentFlags }) => {
	const { t } = useTranslation()
	const dialogStore = useDialogContext()
	if (!dialogStore) {
		throw new Error('Dialog must be wrapped in DialogProvider')
	}

	const [selectedFlags, setSelectedFlags] =
		useState<ExperienceFlag[]>(currentFlags)

	const updateExperienceVettingImageIsApproved =
		useUpdateExperienceVettingImageIsApproved({
			experienceId,
			galleryId,
			imageId: image.id
		})

	const addExperienceGalleryImageFlags = useAddExperienceGalleryImageFlags({
		experienceId,
		sectionId: galleryId
	})
	const removeExperienceGalleryImageFlags =
		useRemoveExperienceGalleryImageFlags({
			experienceId,
			sectionId: galleryId
		})

	const { data: flags, isLoading: isFlagsLoading } = useGetExperiencesFlags({
		scopes: ['photo_gallery']
	})

	const isSubmitting =
		updateExperienceVettingImageIsApproved.isPending ||
		addExperienceGalleryImageFlags.isPending

	const hasSelectedFlags = selectedFlags.length > 0

	const handleAddFlag = (flag: ExperienceFlag) => {
		setSelectedFlags((prev) => [...prev, flag])
	}

	const handleRemoveFlag = (flag: ExperienceFlag) => {
		if (currentFlags.some((f) => f.id === flag.id)) {
			removeExperienceGalleryImageFlags.mutate({
				flags: [flag],
				itemId: image.id
			})
		}
		setSelectedFlags((prev) => prev.filter((f) => f.id !== flag.id))
	}

	const handleReject = async () => {
		try {
			// First, mark the image as rejected
			await updateExperienceVettingImageIsApproved.mutateAsync({
				isApproved: false
			})

			// If there are flags, add them
			if (selectedFlags.length > 0) {
				await addExperienceGalleryImageFlags.mutateAsync({
					flags: selectedFlags,
					itemId: image.id
				})
			}
		} catch (error) {
			console.error('Error rejecting image:', error)
		} finally {
			dialogStore.hide()
		}
	}

	return (
		<>
			<DialogHeader>
				<DialogDismiss disabled={isSubmitting} />
				<DialogHeading>
					{t('vetting:criteria.commons.select_flags')}
				</DialogHeading>
			</DialogHeader>
			<OpsVettingRejectDialogBody>
				<GalleryImageItem src={image.url} />
				<ScrollArea scrollbar={<Scrollbar />}>
					<div className='flex flex-col gap-2'>
						{isFlagsLoading
							? Array.from(
									{ length: 10 },
									(_, i) => `skeleton-${i}`
								).map((key) => (
									<div className='flex gap-2' key={key}>
										<Skeleton height={20} width={20} />
										<Skeleton height={20} width='100%' />
									</div>
								))
							: flags?.map((flag) => (
									<OpsVettingRejectDialogBodyFlagsItem
										gap={2}
										key={flag.id}
									>
										<Checkbox
											checked={selectedFlags.some(
												(f) => f.id === flag.id
											)}
											onChange={() => {
												if (
													selectedFlags.some(
														(f) => f.id === flag.id
													)
												) {
													handleRemoveFlag(flag)
												} else {
													handleAddFlag(flag)
												}
											}}
										/>
										<p>
											{t(
												experiencesFlagsMapToLabel[
													flag.keyName
												]
											)}
										</p>
									</OpsVettingRejectDialogBodyFlagsItem>
								))}
					</div>
				</ScrollArea>
			</OpsVettingRejectDialogBody>
			<DialogFooter>
				<ButtonLegacy
					disabled={isSubmitting}
					onClick={() => dialogStore.hide()}
					type='button'
					variant='tertiary'
				>
					{t('common:cancel')}
				</ButtonLegacy>

				<ButtonLegacy
					disabled={isSubmitting || !hasSelectedFlags}
					onClick={handleReject}
					type='button'
				>
					{t('vetting:criteria.commons.reject_image')}
					{isSubmitting && <Spinner size={20} />}
				</ButtonLegacy>
			</DialogFooter>
		</>
	)
}
